<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  DgrIcon,
  DgrSelectbox,
  SelectboxItem,
} from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import AdminContent from '@/components/layouts/admin-content.vue';
import Header from '@/components/layouts/header.vue';
import { ZERO_CREDIT_BALANCE_WARNING_MESSAGE } from '@/utils/credit/constants';
import { formatDate } from '@/utils/formatters';
import {
  STATES,
  useManagementContract,
  useManagementContractCreditPeriods,
} from '@/utils/swr';

const route = useRoute();
const router = useRouter();
const { createSnackbar } = useSnackbar();
const contractId = computed(() => Number(route.params.contractId));
const { data: managementContract } = useManagementContract(contractId);
const {
  data: creditPeriodsData,
  state: creditPeriodsState,
  error: creditPeriodsError,
} = useManagementContractCreditPeriods(contractId);
const isLoadingCreditPeriods = computed(
  () =>
    creditPeriodsState.value === STATES.PENDING ||
    creditPeriodsState.value === STATES.VALIDATING,
);

const contract = computed(() => managementContract.value?.management_contract);
const contractName = computed(() => contract.value?.contract_name ?? '');
const creditPeriods = computed(() => creditPeriodsData.value ?? []);

const creditPeriodOptions = computed<SelectboxItem<number>[]>(() => {
  return creditPeriods.value.map(period => ({
    value: period.id,
    label: `${formatDate(period.start_date)}~${formatDate(period.end_date)}`,
  }));
});

const selectedCreditPeriodId = ref<number | undefined>(undefined);

// 契約期間リストが読み込まれたら、最初のアイテムをデフォルト値として選択させる
watch(creditPeriodsState, newState => {
  if (
    newState === STATES.SUCCESS &&
    selectedCreditPeriodId.value === undefined &&
    creditPeriods.value.length > 0
  ) {
    selectedCreditPeriodId.value = creditPeriods.value[0].id;
  }
});

watch(
  () => creditPeriodsError?.value,
  err => {
    if (err?.response?.status === 404) {
      createSnackbar({
        message: '指定された契約は存在しません',
        type: 'error',
      });
      router.replace({ name: 'anewsHome' });
    }
  },
);

const selectedCreditPeriod = computed(() => {
  return creditPeriods.value.find(p => p.id === selectedCreditPeriodId.value);
});

const creditBalance = computed(() => {
  return selectedCreditPeriod.value?.balance ?? 0;
});

const isSelectedCreditPeriodExpired = computed(() => {
  if (!selectedCreditPeriod.value) return false;
  return dayjs(selectedCreditPeriod.value.end_date).isBefore(dayjs(), 'day');
});

const shouldShowZeroCreditBalanceWarning = computed(() => {
  return !isSelectedCreditPeriodExpired.value && creditBalance.value === 0;
});

const tabs = computed(() => {
  return [
    {
      name: '利用履歴',
      linkTo: {
        name: 'contractCreditsUsages',
        params: { contractId: contractId.value },
      },
    },
    {
      name: '付与履歴',
      linkTo: {
        name: 'contractCreditsGrants',
        params: { contractId: contractId.value },
      },
    },
  ];
});
</script>

<template>
  <div class="contract-credit">
    <Header
      :title="`${contractName}のクレジット`"
      :tabs="tabs"
      :align-left="true"
      content-width="100%"
    >
      <template #titleMenu>
        <div class="period-selector" v-if="!isLoadingCreditPeriods">
          <label class="c-text-regular--s label">契約期間：</label>
          <DgrSelectbox
            :options="creditPeriodOptions"
            v-model="selectedCreditPeriodId"
            size="small"
          />
        </div>
      </template>
      <template #detail-content
        ><div class="credit-balance">
          <div>残高：{{ creditBalance.toLocaleString() }}</div>
          <div
            v-if="isSelectedCreditPeriodExpired && creditBalance > 0"
            class="expired-label"
          >
            失効
          </div>
          <DgrIcon
            v-if="shouldShowZeroCreditBalanceWarning"
            v-tooltip.bottom="{
              content: ZERO_CREDIT_BALANCE_WARNING_MESSAGE,
            }"
            size="small"
            name="exclamation-circle"
          />
        </div>
      </template>
    </Header>
    <AdminContent class="admin-content">
      <router-view
        :selected-credit-period-id="selectedCreditPeriodId"
      ></router-view>
    </AdminContent>
  </div>
</template>

<style lang="scss" scoped>
.contract-credit {
  .admin-content {
    position: relative; // 子要素で使用されるLoadingコンポーネントの表示位置の基準にするためposition: relativeを指定
  }
  width: 100%;

  .period-selector {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    margin-left: 4px;
    .label {
      color: $color-gray800;
      font-size: 14px;
    }
  }
  .credit-balance {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    .expired-label {
      padding: 2px 8px;
      background-color: $color-gray200;
      border-radius: 2px;
    }
  }
}
</style>
